import {create} from '@github-ui/dotcom-schema/protobuf'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {
  type LabelsResponse,
  LabelsResponseSchema,
  type PullRequestLabel,
  PullRequestLabelSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/labels'
import type {LegacyLabel, LegacyLabelsData} from '../payloads/labels'

/**
 * TEMPORARY — proto migration shim for the `labels` page-data endpoint.
 *
 * The generated `LabelsResponse` message is the canonical shape that all consumers
 * code against. When the backend `labels_proto` flag is ON the endpoint returns
 * proto3-JSON, which `queryFnFetch` decodes (via the `X-Proto-JSON` header + the
 * generated `LabelsResponseSchema`) straight into a `LabelsResponse` — we pass it
 * through untouched. When the flag is OFF the endpoint returns the legacy JSON
 * shape, which we normalize UP to the proto shape so downstream code only ever
 * sees `LabelsResponse`.
 *
 * We always normalize legacy → proto (never the other way around). Once the flag
 * is fully rolled out and the legacy backend branch is removed, delete this whole
 * module and pass `LabelsResponseSchema` to the route directly.
 */

function protoLabel(label: LegacyLabel): PullRequestLabel {
  return create(PullRequestLabelSchema, {
    id: BigInt(label.id),
    name: label.name,
    color: label.color,
    // Legacy encodes a missing description as null; proto3 uses the empty string.
    description: label.description ?? '',
  })
}

// Exported for unit testing; the route consumes `labelsQueryFn` below.
export function normalizeLabels(data: LegacyLabelsData): LabelsResponse {
  return create(LabelsResponseSchema, {
    labels: data.labels.map(protoLabel),
  })
}

// Proto-decoded messages carry a `$typeName` discriminator; legacy JSON does not.
function isProtoLabels(response: LabelsResponse | LegacyLabelsData): response is LabelsResponse {
  return '$typeName' in response
}

/**
 * `queryFn` for the `labels` page-data query. Decodes proto responses against the
 * generated schema and passes them through; normalizes legacy JSON responses up to
 * the proto `LabelsResponse` shape.
 */
export async function labelsQueryFn(queryKey: Parameters<typeof queryFnFetch>[0]): Promise<LabelsResponse> {
  const response = await queryFnFetch<typeof LabelsResponseSchema, LegacyLabelsData>(queryKey, {
    schema: LabelsResponseSchema,
  })
  return isProtoLabels(response) ? response : normalizeLabels(response)
}
